// Unit-sized entity geometries, built once and shared. Per-entity sizing
// happens through (instance) transforms, never geometry rebuilds.
//
// Conventions: ground decals are unit quads in the XZ plane (lying flat);
// directional parts (fin, shield) are authored pointing along +z and
// rotated by yaw at update time.

import {
  BufferGeometry,
  ConeGeometry,
  CylinderGeometry,
  Float32BufferAttribute,
  PlaneGeometry,
  SphereGeometry,
  TorusGeometry,
} from "three/webgpu";

/** Unit hemisphere dome sitting on the ice (radius 1, flat side down). */
export function buildDomeGeometry(): BufferGeometry {
  return new SphereGeometry(1, 28, 14, 0, Math.PI * 2, 0, Math.PI / 2);
}

/** Unit ground-decal quad in the XZ plane, spanning ±1. UVs centered. */
export function buildDecalQuadGeometry(): BufferGeometry {
  const geo = new PlaneGeometry(2, 2);
  geo.rotateX(-Math.PI / 2);
  return geo;
}

/**
 * Direction fin: a squat wedge pointing along +z, base at the origin.
 * Length/width 1 — scaled per entity.
 */
export function buildFinGeometry(): BufferGeometry {
  const geo = new ConeGeometry(0.3, 1, 4, 1);
  // Cone points +y by default; tip forward along +z, lying low.
  geo.rotateX(Math.PI / 2);
  geo.translate(0, 0.12, 0.5);
  geo.scale(1, 0.35, 1);
  return geo;
}

/** Control-node: small sphere (unit radius). */
export function buildNodeGeometry(): BufferGeometry {
  return new SphereGeometry(1, 12, 8);
}

/**
 * Tether: a thin quad along +z from 0 to 1 on the ice plane. Scaled in z to
 * the anchor distance and in x for width.
 */
export function buildTetherGeometry(): BufferGeometry {
  const geo = new PlaneGeometry(1, 1);
  geo.rotateX(-Math.PI / 2);
  geo.translate(0, 0, 0.5);
  return geo;
}

/**
 * Trail: a unit quad along +z from 0 to 1 (stretched to the tail length and
 * yawed opposite velocity). UV.y runs 0 at the entity → 1 at the tail tip
 * so the material can fade it out.
 */
export function buildTrailGeometry(): BufferGeometry {
  const geo = new PlaneGeometry(1, 1, 1, 1);
  geo.rotateX(-Math.PI / 2);
  geo.translate(0, 0.02, 0.5);
  return geo;
}

/** Goalie shield: a thick arc band (torus segment) opening along +z. */
export function buildShieldGeometry(halfAngle: number): BufferGeometry {
  const geo = new TorusGeometry(1, 0.09, 8, 24, halfAngle * 2);
  // Torus arc starts at +x in the XY plane; lay flat and center on +z.
  geo.rotateZ(Math.PI / 2 - halfAngle);
  geo.rotateX(-Math.PI / 2);
  return geo;
}

/** Hero puck: squat cylinder, unit radius, height as a fraction of radius. */
export function buildPuckGeometry(): BufferGeometry {
  return new CylinderGeometry(1, 1, 0.38, 24);
}

/** Stun star: a flat 5-point star quad strip in the XZ plane (unit outer radius). */
export function buildStarGeometry(): BufferGeometry {
  const outer = 1;
  const inner = 0.44;
  const positions: number[] = [0, 0, 0];
  const indices: number[] = [];
  for (let i = 0; i < 10; i++) {
    const angle = -Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 === 0 ? outer : inner;
    positions.push(Math.cos(angle) * r, 0, Math.sin(angle) * r);
  }
  for (let i = 1; i <= 10; i++) {
    indices.push(0, i, (i % 10) + 1);
  }
  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}
